import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BsArrowLeft,
  BsBoxSeam,
  BsPencilSquare,
  BsUpcScan,
} from "react-icons/bs";
import JsBarcode from "jsbarcode";

import Sidebar from "../../components/sidebar/Sidebar";
import { serviceGetProductDetails } from "../../api/serviceApi";

import "./inventoryproduct.css";
import { getImageUrl } from "../../utils/helper";

const Product = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const barcodeRef = useRef(null);

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================================================
  // LOAD PRODUCT
  // ==========================================================

  useEffect(() => {
    let cancelled = false;

    const loadProduct = async () => {
      try {
        setLoading(true);

        const product = await serviceGetProductDetails(productId);

        if (!cancelled) {
          setInfo(product);
        }
      } catch (error) {
        console.error("Error loading product:", error);

        if (!cancelled) {
          setInfo(null);

          toast.error(error?.message || "Failed to load product.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [productId]);

  // ==========================================================
  // BARCODE
  // ==========================================================

  useEffect(() => {
    if (!info?.mpn || !barcodeRef.current) {
      return;
    }

    try {
      JsBarcode(barcodeRef.current, info.mpn, {
        format: "CODE128",
        lineColor: "#000000",
        width: 2,
        height: 52,
        displayValue: true,
        fontSize: 13,
        margin: 8,
      });
    } catch (error) {
      console.error("Barcode render error:", error);
    }
  }, [info?.mpn]);

  // ==========================================================
  // PRICING
  // ==========================================================

  const gst = useMemo(() => {
    const cost = Number(info?.cost);

    if (!Number.isFinite(cost) || cost <= 0) {
      return 0;
    }

    return Number((cost * 0.1).toFixed(2));
  }, [info?.cost]);

  const costIncGst = useMemo(() => {
    const cost = Number(info?.cost);

    if (!Number.isFinite(cost) || cost <= 0) {
      return 0;
    }

    return Number((cost + gst).toFixed(2));
  }, [info?.cost, gst]);

  // ==========================================================
  // IMAGE URL
  // ==========================================================

  const imageUrl = getImageUrl(info?.imageUrl);

  // ==========================================================
  // FORMATTERS
  // ==========================================================

  const formatDate = (value) => {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  const formatCurrency = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "$0.00";
    }

    return number.toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    });
  };

  const getQuantityState = () => {
    const quantity = Number(info?.quantity) || 0;
    const minimum = Number(info?.minQuantity) || 0;
    const maximum = Number(info?.maxQuantity) || 0;

    if (minimum > 0 && quantity < minimum) {
      return "low";
    }

    if (maximum > 0 && quantity > maximum) {
      return "high";
    }

    return "normal";
  };

  const goBackHandler = () => {
    navigate("/inventory");
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="view-product-page">
        <Sidebar />

        <main className="view-product-content">
          <div className="view-product-container">
            <div className="view-product-state">
              <div className="view-product-state-icon">
                <BsBoxSeam />
              </div>

              <strong>Loading product...</strong>
              <span>Please wait while the product details are loaded.</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================
  // NOT FOUND
  // ==========================================================

  if (!info) {
    return (
      <div className="view-product-page">
        <Sidebar />

        <main className="view-product-content">
          <div className="view-product-container">
            <div className="view-product-state">
              <div className="view-product-state-icon">
                <BsBoxSeam />
              </div>

              <strong>Product not found</strong>
              <span>
                This product may have been deleted or is no longer available.
              </span>

              <button
                type="button"
                className="view-product-btn primary"
                onClick={goBackHandler}
              >
                <BsArrowLeft />
                Back to Inventory
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const quantityState = getQuantityState();

  return (
    <div className="view-product-page">
      <Sidebar />

      <main className="view-product-content">
        <div className="view-product-container">
          <header className="view-product-header">
            <div>
              <button
                type="button"
                className="view-product-back"
                onClick={goBackHandler}
              >
                <BsArrowLeft />
                Back to Inventory
              </button>

              <h1>Product Details</h1>
              <p>View inventory, pricing and product information.</p>
            </div>

            <Link
              to={`/inventory/update/${productId}`}
              className="view-product-btn primary"
            >
              <BsPencilSquare />
              Edit Product
            </Link>
          </header>

          <div className="view-product-card">
            {/* =====================================================
                LEFT SIDEBAR
            ===================================================== */}

            <aside className="view-product-sidebar">
              <div className="view-product-image">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={info.name || "Product"}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                      event.currentTarget
                        .closest(".view-product-image")
                        ?.classList.add("image-error");
                    }}
                  />
                ) : (
                  <BsBoxSeam />
                )}

                <div className="view-product-image-fallback">
                  <BsBoxSeam />
                </div>
              </div>

              <div className="view-product-summary">
                <strong>{info.name || "Unnamed Product"}</strong>
                <span>{info.type || "Uncategorised product"}</span>
              </div>

              <div
                className={`view-product-stock view-product-stock-${quantityState}`}
              >
                <span>Current Stock</span>
                <strong>{info.quantity ?? 0}</strong>
              </div>

              {info.mpn && (
                <div className="view-product-barcode">
                  <div className="view-product-barcode-title">
                    <BsUpcScan />
                    <strong>Product Barcode</strong>
                  </div>

                  <canvas ref={barcodeRef} />
                </div>
              )}
            </aside>

            {/* =====================================================
                DETAILS
            ===================================================== */}

            <div className="view-product-details">
              <section className="view-product-section">
                <div className="view-product-section-header">
                  <h2>Product Information</h2>
                  <p>Identification, location and supplier details.</p>
                </div>

                <div className="view-product-grid">
                  <div className="view-product-field">
                    <span className="view-product-label">Product Name</span>
                    <strong>{info.name || "—"}</strong>
                  </div>

                  <div className="view-product-field">
                    <span className="view-product-label">MPN</span>
                    <span className="view-product-code">{info.mpn || "—"}</span>
                  </div>

                  <div className="view-product-field">
                    <span className="view-product-label">Type</span>
                    <span>{info.type || "—"}</span>
                  </div>

                  <div className="view-product-field">
                    <span className="view-product-label">Supplier</span>
                    <span>{info.supplier || "—"}</span>
                  </div>

                  <div className="view-product-field">
                    <span className="view-product-label">Shelf Address</span>
                    <span>{info.shelfAddress || "—"}</span>
                  </div>

                  <div className="view-product-field view-product-full">
                    <span className="view-product-label">Description</span>
                    <span className="view-product-description">
                      {info.description || "No description provided."}
                    </span>
                  </div>
                </div>
              </section>

              <div className="view-product-divider" />

              <section className="view-product-section">
                <div className="view-product-section-header">
                  <h2>Pricing</h2>
                  <p>Product costs, GST and selling prices.</p>
                </div>

                <div className="view-product-grid three-col">
                  <div className="view-product-stat">
                    <span>Cost Ex GST</span>
                    <strong>{formatCurrency(info.cost)}</strong>
                  </div>

                  <div className="view-product-stat">
                    <span>GST</span>
                    <strong>{formatCurrency(gst)}</strong>
                  </div>

                  <div className="view-product-stat">
                    <span>Cost Inc GST</span>
                    <strong>{formatCurrency(costIncGst)}</strong>
                  </div>

                  <div className="view-product-stat">
                    <span>Wholesale</span>
                    <strong>{formatCurrency(info.wholesale)}</strong>
                  </div>

                  <div className="view-product-stat">
                    <span>Retail</span>
                    <strong>{formatCurrency(info.retail)}</strong>
                  </div>
                </div>
              </section>

              <div className="view-product-divider" />

              <section className="view-product-section">
                <div className="view-product-section-header">
                  <h2>Stock Levels</h2>
                  <p>Current stock and configured inventory thresholds.</p>
                </div>

                <div className="view-product-grid three-col">
                  <div className="view-product-stat">
                    <span>Quantity</span>
                    <strong>{info.quantity ?? 0}</strong>
                  </div>

                  <div className="view-product-stat">
                    <span>Minimum Quantity</span>
                    <strong>{info.minQuantity ?? 0}</strong>
                  </div>

                  <div className="view-product-stat">
                    <span>Maximum Quantity</span>
                    <strong>{info.maxQuantity ?? 0}</strong>
                  </div>
                </div>
              </section>

              {(info.addedBy ||
                info.addedAt ||
                info.updatedBy ||
                info.updatedAt) && (
                <>
                  <div className="view-product-divider" />

                  <section className="view-product-section">
                    <div className="view-product-section-header">
                      <h2>Activity</h2>
                      <p>Creation and modification information.</p>
                    </div>

                    <div className="view-product-grid">
                      <div className="view-product-field">
                        {info.addedBy && (
                          <div>
                            <span className="view-product-label">Added By</span>
                            <span>{info.addedBy}</span>
                          </div>
                        )}

                        {info.addedAt && (
                          <div>
                            <span className="view-product-label">Added At</span>
                            <span>{formatDate(info.addedAt)}</span>
                          </div>
                        )}

                        {info.updatedBy && (
                          <div>
                            <span className="view-product-label">
                              Last Updated By
                            </span>
                            <span>{info.updatedBy}</span>
                          </div>
                        )}

                        {info.updatedAt && (
                          <div>
                            <span className="view-product-label">
                              Last Updated At
                            </span>
                            <span>{formatDate(info.updatedAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                </>
              )}

              <div className="view-product-actions">
                <button
                  type="button"
                  className="view-product-btn ghost"
                  onClick={goBackHandler}
                >
                  <BsArrowLeft />
                  Back
                </button>

                <Link
                  to={`/inventory/update/${productId}`}
                  className="view-product-btn primary"
                >
                  <BsPencilSquare />
                  Edit Product
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Product;
